'use client';

import Image from '../image';
import Link from '../link';
import BevSafeText from '../bev-safe-text';
import { useTealium } from '@fshr/context/tealium-context';
import { useRecoTracking } from './analytics/recoTracker';
import { useRecoImpressionBatchObserver } from './hooks/useRecoImpression';
import { ConversationCardData } from './types';

type Props = {
	card: ConversationCardData;
	position: number;
	placement: string;
	onClickNavigate: (url?: string) => void;
};

export function ConversationCardItem({ card, position, placement, onClickNavigate }: Props) {
	const { addToRecommendationsCookie } = useTealium();
	const { trackPropertyRecoClick, trackEnhancementRecoClick } = useRecoTracking();

	const isAncillary = Boolean(card.sku);

	/* ---------- Impression payloads ---------- */
	const propertyImpressionPayload = {
		reco_placement: placement,
		reco_position: String(position),
		reco_property_code: card.propertyOrsCode || 'na',
		reco_property_name: card.propertyOrsCode ?? 'na',
		property_datalake_reco_id: card.datalakeRecoId ?? 'na',
		reco_room_package_code_array: 'na',
		reco_room_code_array: 'na',
	};

	const ancillaryImpressionPayload = {
		reco_placement: placement,
		reco_position: String(position),
		reco_enhancement_sku: card.sku!,
		reco_enhancement_name: card.title,
		reco_enhancement_property_name: card.propertyOrsCode ?? 'na',
		enhancement_datalake_reco_id: card.datalakeRecoId ?? 'na',
	};

	const impressionRef = useRecoImpressionBatchObserver(
		isAncillary ? ancillaryImpressionPayload : propertyImpressionPayload,
		isAncillary ? 'ancillary' : 'property'
	);

	/* ---------- Click handler ---------- */
	const handleClick = (event: React.MouseEvent) => {
		event.preventDefault();

		if (isAncillary) {
			addToRecommendationsCookie({
				ancillary: card.sku,
				datalakeRecoId: [
					{
						sku: card.sku!,
						datalakeRecoId: card.datalakeRecoId ?? 'na',
						ors: card.propertyOrsCode,
						type: 'ancillary',
					},
				],
			});
			trackEnhancementRecoClick({
				sku: card.sku!,
				name: card.title,
				propertyCode: card.propertyOrsCode,
				propertyName: card.propertyName ?? 'na',
				enhancementDatalakeRecoId: card.datalakeRecoId ?? 'na',
				placement,
				position,
			});
		} else {
			addToRecommendationsCookie({
				property: card.propertyOrsCode,
				datalakeRecoId: [
					{
						sku: '',
						datalakeRecoId: card.datalakeRecoId ?? 'na',
						ors: card.propertyOrsCode,
						type: 'property',
					},
				],
			});
			trackPropertyRecoClick({
				propertyCode: card.propertyOrsCode,
				propertyName: card.propertyName ?? 'na',
				propertyDatalakeRecoId: card.datalakeRecoId ?? 'na',
				placement,
				position,
			});
		}

		onClickNavigate(card.url);
	};

	return (
		<div ref={impressionRef} className="flex flex-row w-[280px] md:w-[400px] h-full">
			<div className="flex flex-row w-full bg-surface-raised-subtle gap-[15px] p-[10px] md:p-[15px] h-full">
				<div className="h-[100px] w-[100px] md:w-[120px] lg:h-[120px] min-h-[100px] min-w-[100px] md:min-w-[120px] lg:min-h-[120px]">
					<Image
						src={card.image}
						alt={card.title}
						className="w-full h-full object-cover"
						roughSize="s"
					/>
				</div>

				<div className="flex flex-col gap-[15px] w-full">
					<div className="flex flex-col gap-[6px]">
						<h3 className="text-text-primary text-subtitle2 md:line-clamp-2">
							<BevSafeText>{card.title}</BevSafeText>
						</h3>
						<p className="hidden md:line-clamp-2 text-body3 text-text-primary">
							<BevSafeText>{card.description}</BevSafeText>
						</p>
					</div>

					<Link href={card.url ?? ''} intent="filled" className="w-full" onClick={handleClick}>
						<BevSafeText>{card.button.label}</BevSafeText>
					</Link>
				</div>
			</div>
		</div>
	);
}
